// BucketManagement.tsx
import { useEffect, useState } from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "../components/ui/form";
import { useAuth } from "../components/features/AuthContext";
import { useToast } from "../hooks/useToast";
import { apiRequest } from "../components/lib/queryClient";
import { Edit, Trash2, Plus, X } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { ApiResponse } from "../components/lib/utils";
import AddStocksModal from "../components/Dashboard/AddStocksModal";
import { CustomModelAddStocks } from "../components/CustomAddStocksModal";

const bucketSchema = z.object({
  name: z.string().min(1, "Bucket name is required"),
});

type BucketFormData = z.infer<typeof bucketSchema>;

export interface Bucket {
  _id: string;
  name: string;
  pairs?: string[];
  stocks?: { underlying_symbol?: string }[];
  category: "stocks" | "crypto";
  userId: string;
  type: "user" | "common";
  updatedAt?: string;
}

export default function BucketManagement() {
  const { user, currentTradingMode } = useAuth();
  const { toast } = useToast();
  const [expandedBuckets, setExpandedBuckets] = useState<Record<string, boolean>>({});
  const queryClient = useQueryClient();
  const [activeBucket, setActiveBucket] = useState<Bucket | null>(null);
  const [pairSearch, setPairSearch] = useState("");
  const [pairModalOpen, setPairModalOpen] = useState(false);
  const [selectedPairs, setSelectedPairs] = useState<any[]>([]);

  const {
    data: buckets = [],
    isLoading,
    refetch,
  } = useQuery<Bucket[]>({
    queryKey: ["api/buckets", user?.userId, user?.role, currentTradingMode],
    queryFn: async () => {
      if (!user?.userId || !user?.role || !currentTradingMode) return [];
      const api_url = `api/buckets?userId=${user.userId}&role=${user.role}&category=${currentTradingMode}`;
      const res = await apiRequest<ApiResponse<Bucket[]>>("GET", api_url);
      return res.data;
    },
    enabled: !!user?.userId && !!user?.role && !!currentTradingMode,
  });

  const form = useForm<BucketFormData>({
    resolver: zodResolver(bucketSchema),
    defaultValues: { name: "" },
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<Bucket, "_id" | "pairs" | "updatedAt">) => {
      const queryParams = new URLSearchParams({
        userId: user?.userId || "",
        userRole: user?.role || "",
      }).toString();

      return apiRequest("POST", `api/bucket?${queryParams}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["api/buckets", user?.userId, user?.role, currentTradingMode],
      });
      form.reset();
      toast({ title: "Success", description: "Bucket created" });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create Bucket",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `api/buckets/${id}`, undefined, { "userId": user?.userId, "role": user?.role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["api/buckets"] }),
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete bucket",
        variant: "destructive",
      });
    },
  });

  const addPairsMutation = useMutation({
    mutationFn: async ({ id, pairs }: { id: string; pairs: any[] }) =>
      apiRequest("POST", `api/buckets/${id}/pairs`, {
        pairs,
        userId: user?.userId,
        userRole: user?.role,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api/buckets"] });
      toast({ title: "Updated", description: "Pairs added" });
      setPairModalOpen(false);
      setSelectedPairs([]);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to add pairs",
        variant: "destructive",
      });
    },
  });


  const removePairsMutation = useMutation({
    mutationFn: async ({ id, pairs }: { id: string; pairs: any[] }) =>
      apiRequest("DELETE", `api/buckets/${id}/pair`, { pairs ,  userId: user?.userId,
        userRole: user?.role,}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api/buckets"] });
      toast({ title: "Updated", description: "Pairs removed" });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to remove pairs",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: BucketFormData) => {
    if (!user) return;
    createMutation.mutate({
      name: data.name,
      category: currentTradingMode,
      userId: user.userId,
      type: user.role === "admin" ? "common" : "user",
    });
  };

  const handleAddPairsClick = (bucket: Bucket) => {
    setActiveBucket(bucket);
    setPairModalOpen(true);
  };

  const handleAddPair = (pair: string) => {
    if (!pair) return;
    if (!selectedPairs.includes(pair)) {
      setSelectedPairs((prev) => [...prev, pair]);
    }
    setPairSearch("");
  };

  const handleRemovePair = (pair: any) => {
    setSelectedPairs((prev) => prev.filter((p) => p !== pair));
  };

  const handleRemoveItemFromBucket = (bucket: Bucket, item: any) => {
    const itemValue = currentTradingMode === "stocks" ? item.underlying_symbol : item;
    if (!bucket._id || !itemValue) return;
    removePairsMutation.mutate({
      id: bucket._id,
      pairs: [itemValue],
    });
  };

  const handleSavePairs = (pairs: any[]) => {
    if (activeBucket) {
      addPairsMutation.mutate({ id: activeBucket._id, pairs: pairs });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Bucket</h3>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex gap-2 items-end">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>Bucket Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. Top Stocks" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit">Create</Button>
            </form>
          </Form>
        </CardContent>
      </Card>


      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {buckets.length > 0 &&
          [...buckets]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((bucket) => {
              const isExpanded = expandedBuckets[bucket._id] || false;
              const toggleExpand = () => {
                setExpandedBuckets((prev) => ({
                  ...prev,
                  [bucket._id]: !prev[bucket._id],
                }));
              };

              const displayItems =
                currentTradingMode === "crypto" ? bucket.pairs || [] : bucket.stocks || [];
              const visibleItems: any[] = isExpanded ? displayItems : displayItems.slice(0, 3);

              return (
                <Card key={bucket._id} className="border h-64 flex flex-col justify-between">
                  <CardContent className="pt-4 flex flex-col h-full">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-semibold">{bucket.name}</h4>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => handleAddPairsClick(bucket)}>
                          <Plus className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteMutation.mutate(bucket._id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>

                    <Badge variant="outline" className="mb-2">
                      {bucket.type === "common" ? "Admin" : "User"}
                    </Badge>

                    <div className="text-sm space-y-1 overflow-y-auto flex-1">
                      {visibleItems.length > 0 ? (
                        visibleItems.map((item, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between hover:bg-gray-100 px-2 py-1 rounded"
                          >
                            <span>
                              {currentTradingMode === "crypto" ? item : item.underlying_symbol}
                            </span>
                            <button
                              className="text-xs text-red-500 hover:underline"
                              onClick={() => handleRemoveItemFromBucket(bucket, item)}
                            >
                              Remove
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-400">
                          {currentTradingMode === "crypto" ? "No pairs yet" : "No stocks yet"}
                        </p>
                      )}
                    </div>

                    {displayItems.length > 3 && (
                      <button
                        className="text-xs text-blue-600 mt-2 self-start"
                        onClick={toggleExpand}
                      >
                        {isExpanded ? "Show less" : "Show more"}
                      </button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
      </div>


      {currentTradingMode === "crypto" ? (
        <Dialog open={pairModalOpen} onOpenChange={setPairModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Pairs to Bucket: {activeBucket?.name}</DialogTitle>
            </DialogHeader>
            <Input
              value={pairSearch}
              onChange={(e) => setPairSearch(e.target.value)}
              placeholder="Enter pair (e.g., BTCUSDT)"
            />
            <Button type="button" onClick={() => handleAddPair(pairSearch)} disabled={!pairSearch.trim()}>
              Add Pair
            </Button>
            {selectedPairs.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {selectedPairs.map((pair) => (
                  <Badge key={pair} variant="secondary">
                    {pair}
                    <button
                      onClick={() => handleRemovePair(pair)}
                      className="ml-1 text-xs hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            <div className="flex justify-end mt-4">
              <Button onClick={() => handleSavePairs(selectedPairs)} disabled={selectedPairs.length === 0}>
                Save Pairs
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : (
        <CustomModelAddStocks onAddStocks={handleSavePairs} onClose={() => { setPairModalOpen(false) }} show={pairModalOpen} />
      )}
    </div>
  );
}